import React, { useState, useRef } from "react";
import { Paperclip, Send, FileText, Trash2 } from "lucide-react";
import { surfaceMuted } from "../../../../../lib/design-system";
import { cn } from "../../../../../lib/utils";

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const CommentForm = ({
  placeholder = "Add a comment...",
  onSubmit,
  loading,
  buttonLabel = "Post",
  onCancel,
  className,
}) => {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await onSubmit(text, attachments);
    setText("");
    setAttachments([]);
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setAttachments((prev) => [...prev, ...Array.from(e.target.files)]);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={cn("space-y-3", className)}>
      <textarea
        className={cn(
          "w-full p-3 border border-gray-200 dark:border-gray-700 rounded-sm",
          "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm",
          "focus:outline-none focus:ring-2 focus:ring-accent1/50 focus:border-accent1",
          "transition-colors resize-none"
        )}
        rows="3"
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        required
      />

      {attachments.length > 0 && (
        <div className="space-y-1">
          {attachments.map((file, index) => (
            <div
              key={index}
              className={cn(
                "flex items-center p-2 rounded-sm",
                surfaceMuted,
                "border border-gray-200 dark:border-gray-700"
              )}
            >
              <FileText className="w-4 h-4 text-accent1 mr-2 flex-shrink-0" />
              <div className="flex-grow min-w-0">
                <div className="text-xs sm:text-sm truncate text-gray-900 dark:text-gray-100">
                  {file.name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {formatFileSize(file.size)}
                </div>
              </div>
              <button
                className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1 flex-shrink-0"
                onClick={() =>
                  setAttachments(attachments.filter((_, i) => i !== index))
                }
                type="button"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <div>
          <input
            type="file"
            multiple
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileSelect}
          />
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Attach</span>
          </button>
        </div>
        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading || !text.trim()}
            className={cn(
              "flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-sm",
              "bg-accent1 text-white hover:bg-accent1/90 transition-colors",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            <Send className="w-3.5 h-3.5" />
            {buttonLabel}
          </button>
        </div>
      </div>
    </form>
  );
};

export default React.memo(CommentForm);
